import { CARCASS_MM, HALF_CARCASS_MM } from "./carcass";
import type { InternalItem, WardrobeModule } from "../types";
import { cabinetBaseHeightMm } from "./installation";
import { availableHangingHeight, clothesRequiredHeight } from "./hanging";

/**
 * Штанги углового шкафа (приёмка 08.10): не больше двух, обе в корпусе А
 * вдоль задней стены. Вторая встаёт ниже или выше первой так, чтобы вещи
 * на обеих поместились; вещи верхней заканчиваются над нижней.
 */
export const CORNER_MAX_RAILS = 2;

export const cornerCanAddRail = (module: WardrobeModule) =>
  module.items.filter((item) => item.type === "rail").length < CORNER_MAX_RAILS;

const grid = 32;
const up = (value: number) => Math.ceil(value / grid) * grid;
const down = (value: number) => Math.floor(value / grid) * grid;
const halfHeight = (item: InternalItem) => item.type === "rail" ? 36 : HALF_CARCASS_MM;
export const cornerItemBounds = (module: WardrobeModule, item: InternalItem) => ({
  min: up(Math.max(80, cabinetBaseHeightMm(module) + CARCASS_MM + halfHeight(item))),
  max: down(module.height - 80)
});

export const cornerItemPosition = (module: WardrobeModule, item: InternalItem, desired: number) => {
  const { min, max } = cornerItemBounds(module, item);
  const candidates: number[] = [];
  for (let y = min; y <= max; y += grid) {
    if (module.items.every(other => other.id === item.id ||
      Math.abs(y - other.y) >= halfHeight(item) + halfHeight(other) + 32)) candidates.push(y);
  }
  return candidates.sort((a, b) => Math.abs(a - desired) - Math.abs(b - desired))[0];
};

/** Keep vertical order when resizing; move levels only as much as necessary. */
export const fitCornerItems = (module: WardrobeModule): InternalItem[] | null => {
  const sorted = module.items.map(item => ({ ...item })).sort((a, b) => a.y - b.y);
  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i]!;
    const previous = sorted[i - 1];
    item.y = up(Math.max(Math.round(item.y / grid) * grid, cornerItemBounds(module, item).min,
      previous ? previous.y + halfHeight(previous) + halfHeight(item) + 32 : 0));
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    const item = sorted[i]!;
    const next = sorted[i + 1];
    item.y = down(Math.min(item.y, cornerItemBounds(module, item).max,
      next ? next.y - halfHeight(next) - halfHeight(item) - 32 : Infinity));
    if (item.y < cornerItemBounds(module, item).min) return null;
  }
  const byId = new Map(sorted.map(item => [item.id, item]));
  return module.items.map(item => byId.get(item.id)!);
};

export const cornerMinimumHeight = (module: WardrobeModule) => {
  let previous: InternalItem | undefined;
  for (const source of [...module.items].sort((a, b) => a.y - b.y)) {
    previous = { ...source, y: up(Math.max(cornerItemBounds(module, source).min,
      previous ? previous.y + halfHeight(previous) + halfHeight(source) + 32 : 0)) };
  }
  return Math.max(300, (previous?.y ?? 0) + 80);
};

/** Вещи на каждой штанге — не короче пиджаков (иначе вторая штанга бессмысленна). */
const CORNER_RAIL_MIN_HANGING_MM = clothesRequiredHeight("jackets");

/**
 * Лучшая отметка новой штанги: среди свободных — та, при которой самой
 * короткой одежде из всех штанг остаётся больше всего места.
 */
const bestRailLevel = (module: WardrobeModule, rail: InternalItem, desired: number) => {
  const { min, max } = cornerItemBounds(module, rail);
  let best: { y: number; score: number } | undefined;
  for (let y = min; y <= max; y += grid) {
    if (cornerItemPosition(module, rail, y) !== y) continue;
    const trial = { ...module, items: [...module.items.filter((item) => item.id !== rail.id), { ...rail, y }] };
    const score = Math.min(...trial.items.filter((item) => item.type === "rail")
      .map((item) => availableHangingHeight(trial, item)));
    if (!best || score > best.score || (score === best.score && Math.abs(y - desired) < Math.abs(best.y - desired))) {
      best = { y, score };
    }
  }
  return best;
};

/**
 * Высота новой штанги. Первая — ближе к желаемой отметке. Вторая — только
 * если вещам на обеих хватит места; иначе undefined.
 */
export const cornerRailPlacement = (module: WardrobeModule, rail: InternalItem, desired: number) => {
  const others = module.items.filter((item) => item.type === "rail" && item.id !== rail.id);
  if (!others.length) return cornerItemPosition(module, rail, desired);
  const best = bestRailLevel(module, rail, desired);
  return best && best.score >= CORNER_RAIL_MIN_HANGING_MM ? best.y : undefined;
};

/** Сколько мм высоты не хватает для второй штанги (0 — хватает). */
export const cornerSecondRailShortfallMm = (module: WardrobeModule) => {
  if (!module.items.some((item) => item.type === "rail")) return 0;
  const probe: InternalItem = { id: "corner-rail-probe", type: "rail", y: 1650 };
  const best = bestRailLevel(module, probe, probe.y);
  return Math.max(0, Math.ceil(CORNER_RAIL_MIN_HANGING_MM - (best?.score ?? 0)));
};
